import { CategoryWithArtistsType, CompanyLinkType } from '@api';
import {
  TouringCard,
  TouringCardActions,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
  useDeleteCompanyLink,
} from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { GridCol, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  ArtistRosterTable,
  CompanyLinkActions,
  CompanyLinkTable,
  CompanyLinkTitle,
} from './components';
import { useDeleteFreelancer, useFavoriseCompanyLink } from './hooks';

const TRANS_PATH = 'COMPANY_LINKS.COMPANY_LINK_CARD';

type CompanyLinkCardPropsType = {
  personId: string;
  companyLink: CompanyLinkType;
  categoriesWithArtists: CategoryWithArtistsType[];
  onEdit: () => void;
};

export const CompanyLinkCard: React.FC<CompanyLinkCardPropsType> = ({
  personId,
  companyLink,
  categoriesWithArtists,
  onEdit,
}) => {
  const { t } = useTranslation('personDetailsGeneral');

  const { handleFavoriseCompanyLink } = useFavoriseCompanyLink();
  const { handleDeleteCompanyLink } = useDeleteCompanyLink();
  const { handleDeleteFreelancer } = useDeleteFreelancer();
  const { hasPermission } = usePermissionContext();

  const handleDelete = () => {
    if (companyLink.type === 'BUSINESS') {
      handleDeleteCompanyLink(companyLink.linkId);
    } else {
      handleDeleteFreelancer(personId);
    }
  };

  const handleFavorise = () => {
    handleFavoriseCompanyLink({
      personId,
      linkId: companyLink.linkId,
      type: companyLink.type,
    });
  };

  const title =
    companyLink.type === 'FREELANCER'
      ? t(`${TRANS_PATH}.FREELANCER_TITLE`)
      : (companyLink.companyName ?? '');

  const userCanEditPerson = hasPermission({
    permission: 'ADDRESSBOOK_PERSONS',
    action: 'WRITE',
  });

  return (
    <>
      <GridCol span={{ base: 12, sm: 6, md: 4 }}>
        <Stack data-testid="company-link-card">
          <TouringCard>
            <TouringCardHeader>
              <TouringCardTitle>
                <CompanyLinkTitle companyLink={companyLink} title={title} />
              </TouringCardTitle>

              {userCanEditPerson && (
                <TouringCardActions>
                  <CompanyLinkActions
                    isDefaultContact={companyLink.isDefaultContact}
                    onEditClick={onEdit}
                    onFavoriseClick={handleFavorise}
                    onDeleteClick={handleDelete}
                  />
                </TouringCardActions>
              )}
            </TouringCardHeader>
            <TouringCardSection>
              <CompanyLinkTable companyLink={companyLink} />
            </TouringCardSection>
          </TouringCard>
          {categoriesWithArtists.length > 0 && (
            <TouringCard>
              <TouringCardHeader>
                <TouringCardTitle>
                  {t(`${TRANS_PATH}.ARTIST_ROSTER_TITLE`)}
                </TouringCardTitle>
              </TouringCardHeader>
              <TouringCardSection>
                <ArtistRosterTable
                  categoriesWithArtists={categoriesWithArtists}
                />
              </TouringCardSection>
            </TouringCard>
          )}
        </Stack>
      </GridCol>
    </>
  );
};
